import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";
import { Button } from "components/ui/button";

export function UploadProgress({ upload, onCancel, className = "" }) {
  const { language } = useLanguage();
  const tr = translations[language].uploadProgress;

  if (!upload?.busy && upload?.phase !== "completed") return null;

  const progress = upload.phase === "completed" ? 100 : Math.max(0, Math.min(100, Math.round(upload.progress || 0)));
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <div className={`flex w-full flex-col items-center gap-4 text-center ${className}`} role="status" aria-live="polite">
      <div
        className="relative size-28"
        role="progressbar"
        aria-label={tr.progressLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <svg className="size-full -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="currentColor" strokeWidth="7" className="text-white/10" />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className={`text-primary transition-[stroke-dashoffset] duration-300 ${upload.phase === "creating" ? "animate-pulse" : ""}`}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-mono text-xl font-semibold tabular-nums text-foreground">
          {progress}%
        </span>
      </div>

      <div className="min-w-0 max-w-full">
        <p className="text-sm font-medium text-foreground">{tr.phases[upload.phase] || tr.phases.uploading}</p>
        {upload.filename && <p className="mt-1 truncate text-xs text-muted-foreground">{upload.filename}</p>}
      </div>

      {upload.busy && upload.phase !== "cancelling" && (
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          {tr.cancelUpload}
        </Button>
      )}
    </div>
  );
}
